import { Card, Col, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { convertDoubleToCurrencyPTBR } from "../../../utils/ConverterUtils";
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import axiosInstance from '../../../services/AxiosInstance';
import Api from '../../../constants/Api';
import DashboardHeader, { DashboardHeaderMetric } from '../Common/DashboardHeader';
import DashboardSectionHeader from '../Common/DashboardSectionHeader';
import HintTooltip from '../Common/HintTooltip';
import { PanelLoading, Placeholder } from '../Common/LoadingPlaceholder';
import EmployeeSelect from '../Custom/Main/Employee/EmployeeSelect';
import { useBranch } from '../../../context/BranchContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { hasModuleAccess } from '../../../utils/ModuleAccessUtils';
import Module from '../../../constants/Module';
import { useTranslation } from 'react-i18next';
import './Home.css';

const DASHBOARD_COLORS = {
    ink: '#241e19',
    slate: '#70665d',
    muted: '#988c81',
    border: '#e8ddd2',
    surface: '#ffffff',
    surfaceAlt: '#faf7f3',
    sky: '#fc8019',
    skyStrong: '#d85d08',
    teal: '#c98235',
    green: '#3aa67f',
    amber: '#ffb000',
    coral: '#d96c45',
    red: '#d95c5c',
    purple: '#9b7454',
    // Dupla validada para a barra de composição do faturamento: passa nos testes de
    // separação para daltonismo e de contraste nos temas claro e escuro.
    markOrders: '#e5660d',
    markSubscriptions: '#3aa67f',
};

const CATEGORY_PALETTE = [
    '#fc8019',
    '#ffb000',
    '#64805c',
    '#d96c45',
    '#9b7454',
    '#403a36',
];

const PERIOD_OPTIONS = [
    { key: 'today', label: 'Hoje' },
    { key: 'last7', label: '7 dias' },
    { key: 'last30', label: '30 dias' },
    { key: 'month', label: 'Mês atual' },
    { key: 'lastMonth', label: 'Mês anterior' },
    { key: 'year', label: 'Ano atual' },
    { key: 'custom', label: 'Personalizado' },
];

const EMPTY_KPI = { current: 0, previous: 0, growth: 0 };
const EMPTY_BI = {
    revenue: EMPTY_KPI,
    orders: EMPTY_KPI,
    customers: EMPTY_KPI,
    avgTicket: EMPTY_KPI,
    topServices: [],
    topProducts: [],
    serviceRevenue: 0,
    productRevenue: 0,
    depositRevenue: 0,
    topCategories: [],
    employeeProduction: [],
    occupancy: {
        rate: 0,
        previousRate: 0,
        occupiedMinutes: 0,
        availableMinutes: 0,
        blockedMinutes: 0,
        serviceProviders: 0,
        pendingAppointments: 0,
        branchesWithoutHours: [],
        employees: [],
        branches: [],
        heatmap: [],
    },
    hourlyPerformance: [],
    weeklyTrend: [],
    financialMetrics: {
        totalPayableToday: 0,
        totalPayable7Days: 0,
        totalPayable30Days: 0,
        overduePayables: 0,
        totalReceivableToday: 0,
        totalReceivable7Days: 0,
        totalReceivable30Days: 0,
        overdueReceivables: 0,
    },
};

// Bloco de topo: carteira de planos, faturamento consolidado e série dos últimos 12 meses.
const EMPTY_REVENUE_OVERVIEW = {
    plans: {
        activePlans: 0,
        soldInMonth: 0,
        canceledInMonth: 0,
        receivedInMonth: 0,
        averagePaidValue: 0,
    },
    monthlyRevenue: 0,
    yearRevenue: 0,
    previousMonthRevenue: 0,
    previousMonthLabel: '',
    totalRevenue: 0,
    timeline: [],
};

const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseInputDate = (value) => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
};

const buildApiRange = (start, end) => ({
    startDate: formatInputDate(start),
    endDate: formatInputDate(end),
    start: `${formatInputDate(start)}T00:00:00`,
    end: `${formatInputDate(end)}T23:59:59`,
});

const getPresetRange = (presetKey) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (presetKey) {
        case 'today':
            return buildApiRange(today, today);
        case 'last7': {
            const start = new Date(today);
            start.setDate(start.getDate() - 6);
            return buildApiRange(start, today);
        }
        case 'last30': {
            const start = new Date(today);
            start.setDate(start.getDate() - 29);
            return buildApiRange(start, today);
        }
        case 'month': {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return buildApiRange(start, end);
        }
        case 'lastMonth': {
            const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const end = new Date(today.getFullYear(), today.getMonth(), 0);
            return buildApiRange(start, end);
        }
        case 'year': {
            const start = new Date(today.getFullYear(), 0, 1);
            return buildApiRange(start, today);
        }
        default:
            return buildApiRange(today, today);
    }
};

const getPeriodLabel = (presetKey, range) => {
    const option = PERIOD_OPTIONS.find((item) => item.key === presetKey);
    if (presetKey !== 'custom') {
        return option?.label || 'Período';
    }

    const formatter = new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

    return `${formatter.format(parseInputDate(range.startDate))} - ${formatter.format(parseInputDate(range.endDate))}`;
};

const getDaysInRange = (range) => {
    const start = parseInputDate(range.startDate);
    const end = parseInputDate(range.endDate);
    const diff = Math.round((end - start) / 86400000);
    return Math.max(diff + 1, 1);
};

// Todo valor em reais do painel sai com centavos, inclusive nos cards de KPI: arredondar fazia
// a Receita total mostrar "R$ 226.010" enquanto o DRE mostrava 226.009,93.
const fmtCurrency = convertDoubleToCurrencyPTBR;

/**
 * Horário da última atualização. Mostra só a hora no caso normal, em que o painel
 * acabou de carregar; a data volta a aparecer se a leitura for de outro dia.
 */
const formatLastUpdated = (date) => {
    const isToday = date.toDateString() === new Date().toDateString();
    return new Intl.DateTimeFormat('pt-BR', {
        ...(isToday ? {} : { day: '2-digit', month: '2-digit' }),
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

/**
 * Rótulo curto do eixo de valores. Só corta para "k" quando o número é redondo: com
 * arredondamento cego, um tick de 2.500 virava "R$3k" e o eixo passava a mentir.
 */
const fmtAxisCurrency = (value) => {
    if (Math.abs(value) < 1000) {
        return `R$${value}`;
    }

    const thousands = value / 1000;
    const label = Number.isInteger(thousands) ? thousands : thousands.toFixed(1).replace('.', ',');
    return `R$${label}k`;
};

const fmtNumber = (value) => new Intl.NumberFormat('pt-BR').format(value || 0);
const fmtPercent = (value) => `${(value || 0).toFixed(1)}%`;
const buildKpiSpark = (metric) => [metric?.previous || 0, metric?.current || 0];

const getGrowthTone = (value) => {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
};

const TooltipCard = ({ active, payload, label, valueFormatter = (value) => value }) => {
    if (!active || !payload?.length) return null;

    return (
        <div className="dashboard-tooltip">
            {label && <p className="dashboard-tooltip__label">{label}</p>}
            {payload.map((item) => (
                <div key={item.name} className="dashboard-tooltip__row">
                    <span className="dashboard-tooltip__legend">
                        <span className="dashboard-tooltip__dot" style={{ background: item.color }} />
                        {item.name}
                    </span>
                    <strong>{valueFormatter(item.value, item.name)}</strong>
                </div>
            ))}
        </div>
    );
};

/**
 * Balão do gráfico de faturamento por horário. O gráfico desenha só o faturamento; a
 * quantidade de atendimentos vem como texto de apoio, sem ganhar um gráfico próprio.
 * `countKey` indica o campo da contagem na série.
 */
const RevenueWithCountTooltip = ({ active, payload, label, countKey = 'count' }) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;

    return (
        <div className="dashboard-tooltip">
            <p className="dashboard-tooltip__label">{label}</p>
            <div className="dashboard-tooltip__row">
                <span className="dashboard-tooltip__legend">
                    <span className="dashboard-tooltip__dot" style={{ background: payload[0].color }} />
                    Faturamento
                </span>
                <strong>{fmtCurrency(point.revenue)}</strong>
            </div>
            <div className="dashboard-tooltip__row">
                <span className="dashboard-tooltip__legend">Atendimentos</span>
                <strong>{fmtNumber(point[countKey])}</strong>
            </div>
        </div>
    );
};

/**
 * Superfície de destaque reutilizável para os blocos analíticos do dashboard.
 * O tom controla apenas a identidade visual; estrutura e suporte aos temas ficam centralizados.
 */
const SpotlightCard = ({ tone = 'sky', className = '', children }) => (
    <Card
        className={`dashboard-panel dashboard-panel--spotlight dashboard-panel--spotlight-${tone} border-0 shadow-sm h-100 ${className}`.trim()}
    >
        {children}
    </Card>
);

const BorderedCard = ({ className = '', children }) => (
    <Card className={`dashboard-panel dashboard-panel--bordered shadow-sm h-100 ${className}`.trim()}>
        {children}
    </Card>
);

const KpiCard = ({ label, value, growth, icon, accentClass, context, sparkData, details = [], loading = false }) => {
    const tone = getGrowthTone(growth);
    const gradientId = `spark-${label.replace(/\s+/g, '-').toLowerCase()}`;
    const trendIcon = tone === 'negative'
        ? 'fa-arrow-trend-down'
        : tone === 'neutral'
            ? 'fa-minus'
            : 'fa-arrow-trend-up';

    return (
        <Card className={`dashboard-panel dashboard-kpi dashboard-kpi--${accentClass} shadow-sm h-100`}>
            <Card.Body>
                <div className="dashboard-kpi__top">
                    <div className="dashboard-kpi__heading">
                        <p className="dashboard-kpi__label">{label}</p>
                        <h3 className="dashboard-kpi__value" aria-busy={loading || undefined}>
                            {loading ? (
                                <>
                                    <Placeholder width="9rem" height="1.8rem" />
                                    <span className="visually-hidden">Carregando</span>
                                </>
                            ) : value}
                        </h3>
                    </div>
                    <div className={`dashboard-kpi__icon dashboard-accent-${accentClass}`}>
                        <i className={`fa-solid ${icon}`} />
                    </div>
                </div>
                <div className="dashboard-kpi__meta">
                    {loading ? (
                        <>
                            <Placeholder width="11rem" height="1.4rem" />
                            <Placeholder width="7rem" height="0.8rem" />
                        </>
                    ) : (
                        <>
                            <span className={`dashboard-badge dashboard-badge--${tone}`}>
                                <i className={`fa-solid ${trendIcon}`} />
                                {fmtPercent(Math.abs(growth || 0))} vs. período anterior
                            </span>
                            {context && <span className="dashboard-kpi__context">{context}</span>}
                        </>
                    )}
                </div>
                {details.length > 0 && (
                    <div className="dashboard-kpi__details">
                        {details.map((detail) => (
                            <DashboardHeaderMetric
                                key={detail.label}
                                label={detail.label}
                                value={detail.value}
                                tone={detail.tone}
                                variant="card"
                            />
                        ))}
                    </div>
                )}
                <div className="dashboard-kpi__spark">
                    {loading ? <Placeholder width="100%" height="64px" /> : (
                    <ResponsiveContainer width="100%" height={64}>
                        <AreaChart data={sparkData.map((value, index) => ({ index, value }))} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="currentColor" stopOpacity={0.22} />
                                    <stop offset="95%" stopColor="currentColor" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <Area
                                type="monotone"
                                dataKey="value"
                                stroke="currentColor"
                                strokeWidth={2.4}
                                fill={`url(#${gradientId})`}
                                dot={false}
                                isAnimationActive={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

/**
 * Variação de um indicador contra o mesmo intervalo do mês anterior.
 *
 * A direção do sinal e o significado dela são coisas separadas: cancelamento subindo é
 * ruim, então quem chama informa por `higherIsBetter` como a cor deve ser lida. O ícone
 * e o texto acompanham a cor, para o estado nunca depender só dela.
 */
const StatDelta = ({ current, previous, note, higherIsBetter = true, format = fmtNumber }) => {
    const diff = (current || 0) - (previous || 0);
    const direction = diff === 0 ? 'flat' : diff > 0 ? 'up' : 'down';
    const tone = direction === 'flat' ? 'neutral' : (diff > 0) === higherIsBetter ? 'positive' : 'negative';
    const icon = direction === 'flat' ? 'fa-minus' : direction === 'up' ? 'fa-arrow-up' : 'fa-arrow-down';
    const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';

    return (
        <span className={`dashboard-headline__delta dashboard-headline__delta--${tone}`}>
            <i className={`fa-solid ${icon}`} />
            {direction === 'flat' ? 'estável' : `${sign}${format(Math.abs(diff))}`}
            {note && <small>{note}</small>}
        </span>
    );
};

/**
 * Barra de composição do faturamento do mês. As duas partes são separadas por uma fresta
 * na cor da superfície, e não por borda, e a legenda repete os valores em texto para a
 * leitura não depender da cor.
 */
const CompositionMeter = ({ segments }) => {
    // Uma barra com uma parte só não compara nada: some junto com a parte zerada.
    const present = segments.filter((segment) => (segment.value || 0) > 0);
    const total = present.reduce((sum, segment) => sum + segment.value, 0);

    if (present.length < 2 || total <= 0) {
        return null;
    }

    return (
        <div className="dashboard-meter">
            <div className="dashboard-meter__track">
                {present.map((segment) => (
                    <span
                        key={segment.label}
                        className="dashboard-meter__fill"
                        style={{ width: `${(segment.value / total) * 100}%`, background: segment.color }}
                    />
                ))}
            </div>
            <div className="dashboard-meter__legend">
                {present.map((segment) => (
                    <span key={segment.label} className="dashboard-meter__legend-item">
                        <span className="dashboard-meter__dot" style={{ background: segment.color }} />
                        {segment.label}
                        <strong>{fmtCurrency(segment.value)}</strong>
                        <small>{fmtPercent((segment.value / total) * 100)}</small>
                    </span>
                ))}
            </div>
        </div>
    );
};

/**
 * Ranking de um tipo de item (serviços ou produtos). Cada linha mostra faturamento e
 * quantidade, porque a comanda de assinante guarda só o que ele pagou a mais: o serviço
 * mais feito da casa pode ter faturamento modesto e continuar sendo o carro-chefe.
 * A barra de fundo acompanha a participação do item no faturamento do próprio tipo.
 */
const ItemRanking = ({ title, icon, items, emptyText }) => (
    <div className="dashboard-ranking">
        <p className="dashboard-ranking__title">
            <i className={`fa-solid ${icon}`} />
            {title}
        </p>
        {items.length > 0 ? (
            <ol className="dashboard-ranking__list">
                {items.map((item, index) => (
                    <li key={item.name} className="dashboard-ranking__row">
                        <span className="dashboard-ranking__position">{index + 1}</span>
                        <div className="dashboard-ranking__body">
                            <div className="dashboard-ranking__line">
                                <strong className="dashboard-ranking__name">{item.name}</strong>
                                <strong className="dashboard-ranking__value">{fmtCurrency(item.value)}</strong>
                            </div>
                            <div className="dashboard-ranking__line dashboard-ranking__line--meta">
                                <span>{fmtNumber(item.quantity)} {item.quantity === 1 ? 'venda' : 'vendas'}</span>
                                <span>{fmtPercent(item.percent)}</span>
                            </div>
                            <div className="dashboard-ranking__bar">
                                <span style={{ width: `${Math.min(item.percent || 0, 100)}%` }} />
                            </div>
                        </div>
                    </li>
                ))}
            </ol>
        ) : (
            <p className="dashboard-ranking__empty">{emptyText}</p>
        )}
    </div>
);

/** Rótulos curtos dos dias da semana no padrão ISO (1 = segunda-feira). */
const WEEKDAY_NAMES = { 1: 'Segunda-feira', 2: 'Terça-feira', 3: 'Quarta-feira', 4: 'Quinta-feira', 5: 'Sexta-feira', 6: 'Sábado', 7: 'Domingo' };

/**
 * Faixa de leitura da ocupação de uma cadeira. Os limites seguem a leitura de gestão:
 * acima de 85% o barbeiro está no limite e a casa perde cliente; abaixo de 40% a cadeira
 * está parada boa parte do tempo.
 */
const getOccupancyZone = (rate) => {
    if (rate >= 85) return { key: 'full', label: 'No limite' };
    if (rate >= 60) return { key: 'healthy', label: 'Saudável' };
    if (rate >= 40) return { key: 'moderate', label: 'Moderada' };
    return { key: 'idle', label: 'Ociosa' };
};

/** Converte minutos em horas inteiras para exibição. */
const toHours = (minutes) => Math.round((minutes || 0) / 60);

/**
 * Ranking de ocupação por barbeiro, do mais cheio para o mais vazio. O nome da filial só
 * aparece quando o painel está consolidando mais de uma, para não repetir informação.
 */
const EmployeeOccupancyList = ({ employees, showBranch }) => (
    <ol className="dashboard-occupancy-list">
        {employees.map((employee) => {
            const zone = getOccupancyZone(employee.rate || 0);
            return (
                <li key={`${employee.employeeId}-${employee.branchId}`} className="dashboard-occupancy-list__row">
                    <div className="dashboard-occupancy-list__line">
                        <strong className="dashboard-occupancy-list__name">
                            {employee.employeeName}
                            {showBranch && <small>{employee.branchName}</small>}
                        </strong>
                        <strong className="dashboard-occupancy-list__rate">{fmtPercent(employee.rate)}</strong>
                    </div>
                    <div className={`dashboard-occupancy-list__bar dashboard-occupancy-zone--${zone.key}`}>
                        <span style={{ width: `${Math.min(employee.rate || 0, 100)}%` }} />
                    </div>
                    <div className="dashboard-occupancy-list__meta">
                        <span className={`dashboard-occupancy-list__zone dashboard-occupancy-zone--${zone.key}`}>{zone.label}</span>
                        <span>
                            {fmtNumber(toHours(employee.occupiedMinutes))}h de {fmtNumber(toHours(employee.availableMinutes))}h
                            {employee.blockedMinutes > 0 && ` · ${fmtNumber(toHours(employee.blockedMinutes))}h bloqueadas`}
                        </span>
                    </div>
                </li>
            );
        })}
    </ol>
);

/**
 * Mapa de calor de ocupação: linhas são horas, colunas são dias da semana. Só entram as
 * horas e os dias em que houve capacidade, e cada célula escreve o percentual para a leitura
 * não depender da intensidade da cor.
 */
const OccupancyHeatmap = ({ cells }) => {
    const days = [...new Set(cells.map((cell) => cell.dayOfWeek))].sort((a, b) => a - b);
    const hours = [...new Set(cells.map((cell) => cell.hour))].sort((a, b) => a - b);
    const byKey = new Map(cells.map((cell) => [`${cell.dayOfWeek}-${cell.hour}`, cell]));

    return (
        <div className="dashboard-heatmap">
            <table className="dashboard-heatmap__table">
                <thead>
                    <tr>
                        <th scope="col" aria-label="Hora" />
                        {days.map((day) => <th key={day} scope="col">{WEEKDAY_NAMES[day]}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {hours.map((hour) => (
                        <tr key={hour}>
                            <th scope="row">{`${String(hour).padStart(2, '0')}h`}</th>
                            {days.map((day) => {
                                const cell = byKey.get(`${day}-${hour}`);
                                if (!cell) {
                                    return <td key={day} className="dashboard-heatmap__cell dashboard-heatmap__cell--empty" />;
                                }
                                const rate = cell.rate || 0;
                                return (
                                    <td
                                        key={day}
                                        className={`dashboard-heatmap__cell ${rate >= 55 ? 'is-strong' : ''}`}
                                        style={{ backgroundColor: `rgba(216, 93, 8, ${0.06 + (Math.min(rate, 100) / 100) * 0.84})` }}
                                        title={`${WEEKDAY_NAMES[day]} às ${String(hour).padStart(2, '0')}h: ${fmtPercent(rate)} (${fmtNumber(toHours(cell.occupiedMinutes))}h de ${fmtNumber(toHours(cell.availableMinutes))}h)`}
                                    >
                                        {Math.round(rate)}%
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * Faixa de destaque do topo: um título, poucos números grandes e, quando faz sentido, a
 * variação contra o mês anterior. O rodapé recebe contexto extra (barra de composição,
 * indicadores secundários) sem competir com os números principais.
 */
const HeadlineCard = ({ title, hint, stats, loading, footer }) => (
    <Card className="dashboard-panel dashboard-headline shadow-sm">
        <Card.Body>
            <div className="dashboard-headline__head">
                <h6 className="dashboard-headline__title">{title}</h6>
                {hint && (
                    <span className="dashboard-headline__hint">
                        <HintTooltip text={hint} />
                    </span>
                )}
                {loading && <Spinner animation="border" size="sm" className="dashboard-headline__spinner" />}
            </div>
            <div className="dashboard-headline__grid" aria-busy={loading || undefined}>
                {stats.map((stat) => (
                    <div key={stat.label} className="dashboard-headline__stat">
                        <span className="dashboard-headline__label">
                            {stat.icon && <i className={`fa-solid ${stat.icon}`} />}
                            {stat.label}
                        </span>
                        {loading ? (
                            <>
                                <strong className="dashboard-headline__value">
                                    <Placeholder width="70%" height="1.9rem" />
                                    <span className="visually-hidden">Carregando</span>
                                </strong>
                                {stat.delta && <Placeholder width="45%" height="0.8rem" />}
                            </>
                        ) : (
                            <>
                                <strong className="dashboard-headline__value">{stat.value}</strong>
                                {stat.delta}
                            </>
                        )}
                    </div>
                ))}
            </div>
            {footer && (
                <div className="dashboard-headline__footer">
                    {loading ? (
                        <div className="dashboard-headline__footer-loading" aria-hidden="true">
                            <Placeholder width="35%" height="0.8rem" />
                            <Placeholder width="100%" height="0.6rem" />
                        </div>
                    ) : footer}
                </div>
            )}
        </Card.Body>
    </Card>
);


/**
 * Tooltip do gráfico de faturamento. A série desenhada é só o total, então a quebra entre
 * comanda e assinatura é lida do próprio ponto - é ela que explica o número do mês.
 */
const RevenueTooltip = ({ active, payload, showSubscriptions = true }) => {
    if (!active || !payload?.length) return null;

    const point = payload[0].payload || {};
    // O balão traz duas quebras do mesmo Total. A de cima é por origem do dinheiro, comanda
    // e mensalidade. A de baixo é por cadeira: o que alguma cadeira produziu e o que não
    // chegou a nenhuma. As duas fecham no mesmo valor, e é a de baixo que o gestor confere
    // contra o relatório de comissões.
    const chairTotal = Number(point.chairs || 0) + (showSubscriptions ? Number(point.subscriptions || 0) : 0);
    const outsideChairs = Number(point.total || 0) - chairTotal;

    return (
        <div className="dashboard-tooltip">
            <div className="dashboard-tooltip__row">
                <span className="dashboard-tooltip__legend">Data</span>
                <strong>{point.label}</strong>
            </div>
            <div className="dashboard-tooltip__row">
                <span className="dashboard-tooltip__legend">Comandas</span>
                <strong>{fmtCurrency(point.orders)}</strong>
            </div>
            {showSubscriptions && (
                <div className="dashboard-tooltip__row">
                    <span className="dashboard-tooltip__legend">Assinaturas</span>
                    <strong>{fmtCurrency(point.subscriptions)}</strong>
                </div>
            )}
            {/* Receita lançada em contas a receber com categoria de receita: a mesma parcela do
                DRE. Só aparece no mês que teve, para o balão não ganhar uma linha de zeros. */}
            {Number(point.otherRevenue || 0) !== 0 && (
                <div className="dashboard-tooltip__row">
                    <span className="dashboard-tooltip__legend">Outras receitas</span>
                    <strong>{fmtCurrency(point.otherRevenue)}</strong>
                </div>
            )}
            {/* O Total fecha as parcelas de cima, como numa nota: primeiro o que entrou de
                cada origem, depois a soma. É o valor da linha do gráfico, então é ele que
                ganha o destaque. */}
            <div className="dashboard-tooltip__row dashboard-tooltip__row--total dashboard-tooltip__row--highlight">
                <span className="dashboard-tooltip__legend">Total</span>
                <strong>{fmtCurrency(point.total)}</strong>
            </div>
            {/* Segunda quebra do mesmo Total, agora por cadeira. Somar estas duas linhas dá
                o Total de cima, então o gestor não precisa acreditar: ele confere. */}
            <div className="dashboard-tooltip__row dashboard-tooltip__row--total">
                <span className="dashboard-tooltip__legend">Total das cadeiras</span>
                <strong>{fmtCurrency(chairTotal)}</strong>
            </div>
            <div className="dashboard-tooltip__row">
                <span className="dashboard-tooltip__legend">Fora das cadeiras</span>
                <strong>{fmtCurrency(outsideChairs)}</strong>
            </div>
            <p className="dashboard-tooltip__note">
                O relatório de comissões mostra o total das cadeiras. Fica de fora a venda que
                não chegou a nenhuma: comanda sem colaborador, categoria que não comissiona,
                produção de quem foi inativado, reserva e outras receitas. A venda cancelada em
                outro mês também aparece aqui: no mês da venda e, descontada, no mês do
                cancelamento.
            </p>
        </div>
    );
};

/**
 * Tooltip do ranking de produção. Repete o valor da barra e acrescenta o que ela não
 * mostra: quantos atendimentos geraram aquela receita e como foi contra o período anterior.
 */
const EmployeeProductionTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    const point = payload[0].payload || {};
    const services = point.serviceCategories || [];
    const products = point.products || [];
    const servicesTotal = services.reduce((total, item) => total + Number(item.revenue || 0), 0);
    const productsTotal = products.reduce((total, item) => total + Number(item.revenue || 0), 0);
    const subscriptionTotal = Number(point.subscriptionRevenue || 0);

    return (
        <div className="dashboard-tooltip">
            <p className="dashboard-tooltip__label">{point.name}</p>
            <div className="dashboard-tooltip__breakdown dashboard-tooltip__breakdown--formula">
                <strong>Composição da produção total</strong>
                <div className="dashboard-tooltip__row">
                    <span className="dashboard-tooltip__legend">Serviços</span>
                    <strong>{fmtCurrency(servicesTotal)}</strong>
                </div>
                {services.map((item) => (
                    <div className="dashboard-tooltip__row dashboard-tooltip__row--nested" key={`service-${item.name}`}>
                        <span className="dashboard-tooltip__legend">{item.name}</span>
                        <strong>{fmtCurrency(item.revenue)}</strong>
                    </div>
                ))}
                <div className="dashboard-tooltip__row">
                    <span className="dashboard-tooltip__legend">Produtos</span>
                    <strong>{fmtCurrency(productsTotal)}</strong>
                </div>
                <div className="dashboard-tooltip__row">
                    <span className="dashboard-tooltip__legend">Assinaturas</span>
                    <strong>{fmtCurrency(subscriptionTotal)}</strong>
                </div>
                <div className="dashboard-tooltip__row dashboard-tooltip__row--total">
                    <span className="dashboard-tooltip__legend">Total</span>
                    <strong>{fmtCurrency(point.receita)}</strong>
                </div>
            </div>
        </div>
    );
};

const EmployeeProductionDetail = ({ detail, production }) => {
    const serviceCategories = Object.values((detail?.commissionItems || [])
        .filter((item) => item.product?.type === 'SERVICE')
        .reduce((groups, item) => {
            const name = item.product?.category?.name || 'Sem categoria';
            if (!groups[name]) groups[name] = { name, revenue: 0 };
            groups[name].revenue += Number(item.total || 0);
            return groups;
        }, {}));
    const products = Object.values((detail?.serviceCounts || [])
        .filter((item) => item.productType === 'PRODUCT')
        .reduce((groups, item) => {
            const name = item.productName || 'Produto';
            if (!groups[name]) groups[name] = { name, quantity: 0, revenue: 0 };
            groups[name].quantity += Number(item.quantity || 0);
            groups[name].revenue += Number(item.total || 0);
            return groups;
        }, {}));
    const totalProduction = Number(production?.receita || 0);
    // O rateio de assinatura vem pronto do mesmo cálculo do relatório de comissões.
    // Subtrair a receita do detalhe da produção total dava outro número quando os dois
    // endpoints enxergavam recortes de filial diferentes, e o modal contradizia o hint.
    const subscriptionProduction = Number(production?.subscriptionRevenue || 0);
    const commandRevenue = Math.max(totalProduction - subscriptionProduction, 0);
    const subscriptionScore = (detail?.scoreEntries || []).reduce(
        (total, entry) => total + Number(entry.totalScore || 0), 0);
    const appointments = Number(production?.atendimentos || 0);

    return (
        <div className="dashboard-production-detail">
            <div className="dashboard-production-detail__formula">
                <span>Produção total</span>
                <strong>{fmtCurrency(totalProduction)}</strong>
                <small>{fmtCurrency(commandRevenue)} em comandas + {fmtCurrency(subscriptionProduction)} em assinaturas atribuídas</small>
            </div>
            <div className="dashboard-production-detail__metrics">
                <div><span>Atendimentos</span><strong>{fmtNumber(appointments)}</strong></div>
                <div><span>Itens vendidos</span><strong>{fmtNumber(detail?.serviceCounts?.reduce((sum, item) => sum + Number(item.quantity || 0), 0))}</strong></div>
                <div><span>Pontos de assinatura</span><strong>{fmtNumber(subscriptionScore)}</strong></div>
            </div>

            <h6>Serviços por categoria</h6>
            <Table striped size="sm" responsive>
                <thead><tr><th>Categoria</th><th className="text-end">Faturamento gerado</th></tr></thead>
                <tbody>
                    {serviceCategories.map((item) => <tr key={item.name}><td>{item.name}</td><td className="text-end">{fmtCurrency(item.revenue)}</td></tr>)}
                    {serviceCategories.length === 0 && <tr><td colSpan={2} className="text-center">Nenhum serviço faturado</td></tr>}
                </tbody>
            </Table>

            <h6>Produtos</h6>
            <Table striped size="sm" responsive>
                <thead><tr><th>Produto</th><th className="text-end">Quantidade</th><th className="text-end">Faturamento gerado</th></tr></thead>
                <tbody>
                    {products.map((item) => <tr key={item.name}><td>{item.name}</td><td className="text-end">{fmtNumber(item.quantity)}</td><td className="text-end">{fmtCurrency(item.revenue)}</td></tr>)}
                    {products.length === 0 && <tr><td colSpan={3} className="text-center">Nenhum produto vendido</td></tr>}
                </tbody>
            </Table>

            <div className="dashboard-production-detail__subscription">
                <strong>Assinaturas</strong>
                <span>{fmtCurrency(subscriptionProduction)}</span>
                <small>Rateio proporcional à pontuação do colaborador no período.</small>
            </div>
        </div>
    );
};

/**
 * Divisor de faixa do dashboard. Existe para separar o que é posição de agora do que
 * responde ao filtro: o seletor de período mora no cabeçalho da faixa que ele comanda,
 * e não no topo da página, onde parecia governar a tela inteira.
 */
const SectionBand = ({ title, hint, action, sticky = false }) => (
    <div className={`dashboard-band${sticky ? ' dashboard-band--sticky' : ''}`}>
        <div className="dashboard-band__identity">
            <h2 className="dashboard-band__title">{title}</h2>
            {hint && <p className="dashboard-band__hint">{hint}</p>}
        </div>
        {action && <div className="dashboard-band__action">{action}</div>}
    </div>
);

const EmptyState = ({ icon, title, description }) => (
    <div className="dashboard-empty">
        <i className={`fa-solid ${icon}`} />
        <h6>{title}</h6>
        <p>{description}</p>
    </div>
);

const Home = () => {
    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    // Recharts recebe cor por prop, então os eixos não enxergam as variáveis de tema do CSS.
    // Sem isto, o nome da categoria fica preto sobre fundo escuro e some da tela.
    const { appTheme } = useContext(ThemeContext);
    const isDarkTheme = appTheme === 'dark';
    const axisInk = isDarkTheme ? '#f2eeea' : DASHBOARD_COLORS.ink;
    const axisMuted = isDarkTheme ? '#aaa3a0' : DASHBOARD_COLORS.muted;
    const axisGrid = isDarkTheme ? 'rgba(242, 238, 234, 0.12)' : '#eee5dc';
    // Faturamento, contas a pagar/receber e ranking de colaboradores são dados de
    // gestão. Quem não tem o módulo chega aqui pela URL mesmo com o menu escondido,
    // então a tela não monta nada - e o backend recusa a chamada de qualquer forma.
    const canSeeBusinessOverview = hasModuleAccess(Module.DASHBOARD);
    // Nem toda empresa contrata assinaturas. Card zerado não é informação: é ruído que faz
    // o gestor procurar um problema que não existe. Sem o módulo, o bloco some da tela.
    const canSeeSubscription = hasModuleAccess(Module.SUBSCRIPTION);

    const [companyInfo, setCompanyInfo] = useState(null);
    const [loadingSections, setLoadingSections] = useState({
        overview: false,
        plans: false,
        bi: false,
    });
    // Abre no mesmo recorte do relatório de comissões (mês atual). Enquanto o dashboard
    // abria em 30 dias, a produção por colaborador e a coluna Assinaturas do relatório
    // mostravam valores diferentes para o mesmo colaborador, e a diferença era apenas a
    // janela de datas: quem comparava as duas telas via um erro de cálculo que não existia.
    const [periodPreset, setPeriodPreset] = useState('month');
    const [dateRange, setDateRange] = useState(getPresetRange('month'));
    const [customStartDate, setCustomStartDate] = useState(getPresetRange('month').startDate);
    const [customEndDate, setCustomEndDate] = useState(getPresetRange('month').endDate);
    const [biData, setBiData] = useState(EMPTY_BI);
    const [revenueOverview, setRevenueOverview] = useState(EMPTY_REVENUE_OVERVIEW);
    const [dataWarnings, setDataWarnings] = useState({});
    const [lastUpdated, setLastUpdated] = useState(null);
    const dashboardRequestId = useRef(0);
    const [employeeProductionFilter, setEmployeeProductionFilter] = useState(null);
    const [employeeProductionDetail, setEmployeeProductionDetail] = useState(null);
    const [employeeProductionDetailLoading, setEmployeeProductionDetailLoading] = useState(false);

    useEffect(() => {
        axiosInstance.get(Api.registration.company_info)
            .then((response) => setCompanyInfo(response.data))
            .catch(() => {});
    }, []);

    const loadDashboard = useCallback(() => {
        const branchId = selectedBranch?.id || -1;
        // Ao trocar de filial, uma resposta da filial anterior pode chegar por último.
        // Só a carga mais recente pode alterar os valores e o estado de carregamento.
        const requestId = ++dashboardRequestId.current;
        const isCurrentRequest = () => requestId === dashboardRequestId.current;
        const setSectionLoading = (section, status) => {
            if (isCurrentRequest()) setLoadingSections((current) => ({ ...current, [section]: status }));
        };
        const setWarning = (section, message) => {
            if (isCurrentRequest()) setDataWarnings((current) => ({ ...current, [section]: message }));
        };

        setDataWarnings({});
        setRevenueOverview(EMPTY_REVENUE_OVERVIEW);
        setBiData(EMPTY_BI);
        setLoadingSections({
            overview: true,
            plans: canSeeSubscription,
            bi: true,
        });

        // O bloco de topo não usa o filtro de período: planos falam de hoje/mês corrente e o
        // gráfico é sempre dos últimos 12 meses.
        axiosInstance.get(`${Api.sales.visual_bi}/revenue-overview/${branchId}?includePlans=false`)
            .then((response) => {
                if (!isCurrentRequest()) return;
                const data = response.data || {};
                setRevenueOverview((current) => ({
                    ...current,
                    monthlyRevenue: data.monthlyRevenue || 0,
                    yearRevenue: data.yearRevenue || 0,
                    previousMonthRevenue: data.previousMonthRevenue || 0,
                    previousMonthLabel: data.previousMonthLabel || '',
                    totalRevenue: data.totalRevenue || 0,
                    timeline: data.timeline || [],
                }));
                setLastUpdated(new Date());
            })
            .catch((error) => {
                if (!isCurrentRequest()) return;
                console.error('Erro ao carregar o resumo de planos e faturamento:', error);
                setWarning('overview', 'O resumo de planos e faturamento não pôde ser carregado agora.');
            })
            .finally(() => setSectionLoading('overview', false));

        // A carteira não depende do histórico de faturamento e pode aparecer assim que terminar.
        if (canSeeSubscription) {
            axiosInstance.get(`${Api.sales.visual_bi}/plan-overview/${branchId}`)
                .then((response) => {
                    if (!isCurrentRequest()) return;
                    setRevenueOverview((current) => ({ ...current, plans: response.data || EMPTY_REVENUE_OVERVIEW.plans }));
                })
                .catch(() => setWarning('plans', 'O resumo de assinaturas não pôde ser carregado agora.'))
                .finally(() => setSectionLoading('plans', false));
        }

        axiosInstance.get(`${Api.sales.visual_bi}/dashboard/${branchId}/${dateRange.start}/${dateRange.end}`)
            .then((response) => {
                if (!isCurrentRequest()) return;
                const data = response.data || {};
                const categoryTotal = (data.topCategories || []).reduce((total, item) => total + (item.value || 0), 0);

                setBiData({
                    revenue: data.revenue || EMPTY_KPI,
                    orders: data.orders || EMPTY_KPI,
                    customers: data.customers || EMPTY_KPI,
                    avgTicket: data.avgTicket || EMPTY_KPI,
                    topServices: data.topServices || [],
                    topProducts: data.topProducts || [],
                    serviceRevenue: data.serviceRevenue || 0,
                    productRevenue: data.productRevenue || 0,
                    depositRevenue: data.depositRevenue || 0,
                    topCategories: (data.topCategories || []).map((item, index) => ({
                        ...item,
                        color: item.color || CATEGORY_PALETTE[index % CATEGORY_PALETTE.length],
                        share: categoryTotal > 0 ? ((item.value || 0) / categoryTotal) * 100 : 0,
                    })),
                    hourlyPerformance: (data.hourlyPerformance || []).map((item) => ({
                        hour: item.hour,
                        count: item.value || 0,
                        revenue: item.revenue || 0,
                    })),
                    weeklyTrend: (data.weeklyTrend || []).map((item) => ({
                        day: item.day,
                        revenue: item.revenue || 0,
                        orders: item.ordersCount || 0,
                    })),
                    employeeProduction: data.employeeProduction || [],
                    occupancy: data.occupancy || EMPTY_BI.occupancy,
                    financialMetrics: data.financialMetrics || EMPTY_BI.financialMetrics,
                });
                setLastUpdated(new Date());
            })
            .catch((error) => {
                if (!isCurrentRequest()) return;
                console.error('Erro ao carregar o BI principal:', error);
                setWarning('bi', 'O BI principal não pôde ser carregado agora.');
            })
            .finally(() => setSectionLoading('bi', false));

    }, [canSeeSubscription, dateRange.end, dateRange.start, selectedBranch]);

    useEffect(() => {
        loadDashboard();
        return () => { dashboardRequestId.current += 1; };
    }, [loadDashboard]);

    const handlePresetChange = (key) => {
        if (key === 'custom') {
            setPeriodPreset('custom');
            return;
        }

        const nextRange = getPresetRange(key);
        setPeriodPreset(key);
        setDateRange(nextRange);
        setCustomStartDate(nextRange.startDate);
        setCustomEndDate(nextRange.endDate);
    };

    const applyCustomPeriod = () => {
        if (!customStartDate || !customEndDate || customStartDate > customEndDate) {
            return;
        }

        setPeriodPreset('custom');
        setDateRange({
            startDate: customStartDate,
            endDate: customEndDate,
            start: `${customStartDate}T00:00:00`,
            end: `${customEndDate}T23:59:59`,
        });
    };

    const branchName = selectedBranch?.name || selectedBranch?.description || 'Todas as filiais';
    const periodLabel = getPeriodLabel(periodPreset, dateRange);
    const daysInRange = getDaysInRange(dateRange);
    const planOverview = revenueOverview.plans || EMPTY_REVENUE_OVERVIEW.plans;
    const previousMonthNote = revenueOverview.previousMonthLabel
        ? `vs. ${revenueOverview.previousMonthLabel}`
        : 'vs. mês anterior';
    // A quebra do mês corrente é o último ponto da série, então não precisa de outra fonte.
    const currentMonthPoint = revenueOverview.timeline[revenueOverview.timeline.length - 1]
        || { orders: 0, subscriptions: 0 };
    // Empresa nova só tem meses zerados; nesse caso o vazio comunica melhor que uma linha reta.
    const revenueTimelineHasData = revenueOverview.timeline.some((point) => (point.total || 0) > 0);

    // Ocupação: o percentual sozinho não distingue agenda vazia de equipe grande demais,
    // então a leitura vem acompanhada das horas e do tamanho da equipe que sustenta a conta.
    const occupancy = biData.occupancy || EMPTY_BI.occupancy;
    const occupiedHours = toHours(occupancy.occupiedMinutes);
    const availableHours = toHours(occupancy.availableMinutes);
    const idleHours = Math.max(availableHours - occupiedHours, 0);
    const blockedHours = toHours(occupancy.blockedMinutes);
    const occupancyEmployees = occupancy.employees || [];
    const occupancyBranches = occupancy.branches || [];
    const occupancyHeatmap = occupancy.heatmap || [];
    const branchesWithoutHours = occupancy.branchesWithoutHours || [];

    const trendData = biData.weeklyTrend;
    const hourlyData = biData.hourlyPerformance.filter((item) => item.count > 0 || item.revenue > 0);
    // O dashboard usa a mesma produção total do relatório e permite consultar qualquer
    // colaborador da filial, sem esconder nomes pelo corte artificial de um ranking.
    const employeeProductionData = (biData.employeeProduction || [])
        .filter((employee) => !employeeProductionFilter || employee.employeeId === employeeProductionFilter.value)
        .map((employee, index, list) => ({
        employeeId: employee.employeeId,
        name: employee.employeeName,
        receita: Number(employee.revenue || 0),
        atendimentos: employee.appointments || 0,
        ticket: employee.averageTicket || 0,
        variacao: (employee.revenue || 0) - (employee.previousRevenue || 0),
        serviceCategories: employee.serviceCategories || [],
        products: employee.products || [],
        subscriptionRevenue: Number(employee.subscriptionRevenue || 0),
        // Uma hue só, mais forte no topo: a cor acompanha a magnitude, não a identidade.
        color: `rgba(216, 93, 8, ${1 - (index / Math.max(list.length, 1)) * 0.55})`,
        }));

    const openEmployeeProductionDetail = (production) => {
        const selectedProduction = production?.payload || production;
        if (!selectedProduction?.employeeId) return;
        const branchId = selectedBranch?.id || -1;
        setEmployeeProductionDetail({ production: selectedProduction, detail: null });
        setEmployeeProductionDetailLoading(true);
        axiosInstance.get(`${Api.sales.visual_bi}/employee-production/${selectedProduction.employeeId}/${dateRange.startDate}/${dateRange.endDate}/${branchId}`)
            .then((response) => setEmployeeProductionDetail({ production: selectedProduction, detail: response.data }))
            .catch(() => setEmployeeProductionDetail(null))
            .finally(() => setEmployeeProductionDetailLoading(false));
    };

    const loading = Object.values(loadingSections).some(Boolean);
    const warningMessages = Object.values(dataWarnings).filter(Boolean);

    // O filtro é montado aqui e entregue à faixa de análise: ele só comanda o que está
    // abaixo dela, então não pode continuar morando no cabeçalho da página.
    const periodFilters = (
        <div className="dashboard-scope-filters">
            <div className="dashboard-scope-filters__presets">
                {PERIOD_OPTIONS.map((option) => (
                    <button
                        key={option.key}
                        type="button"
                        className={`dashboard-scope-chip ${periodPreset === option.key ? 'is-active' : ''}`}
                        onClick={() => handlePresetChange(option.key)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>

            {periodPreset === 'custom' && (
                <div className="dashboard-scope-filters__dates">
                    <label>
                        <span>De</span>
                        <input
                            type="date"
                            value={customStartDate}
                            onChange={(event) => setCustomStartDate(event.target.value)}
                        />
                    </label>
                    <label>
                        <span>Até</span>
                        <input
                            type="date"
                            value={customEndDate}
                            onChange={(event) => setCustomEndDate(event.target.value)}
                        />
                    </label>
                    <button type="button" className="dashboard-scope-apply" onClick={applyCustomPeriod}>
                        Aplicar
                    </button>
                </div>
            )}
        </div>
    );

    if (!canSeeBusinessOverview) {
        return (
            <div className="dashboard-bi">
                <Card className="dashboard-bi__no-access">
                    <Card.Body className="text-center py-5">
                        <i className="fa-solid fa-lock fa-2x mb-3 d-block"></i>
                        <h5>{t('access_denied')}</h5>
                        <p className="mb-0">{t('dashboard_no_access_hint')}</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-bi">
            <DashboardHeader
                compact
                title={companyInfo?.name || 'Dashboard de gestão'}
                metrics={[
                    { key: 'branch', label: 'Filial', value: branchName, tone: 'neutral' },
                    ...(lastUpdated ? [{
                        key: 'updated',
                        label: 'Atualizado',
                        value: formatLastUpdated(lastUpdated),
                        tone: 'neutral',
                    }] : []),
                ]}
                action={(
                    /* Atualizar recarrega a tela inteira, então fica no topo. O período
                       desceu para a faixa de análise, que é o alcance real dele. */
                    <button type="button" className="dashboard-header-action dashboard-refresh" onClick={loadDashboard} disabled={loading}>
                        <i className={`fa-solid fa-rotate-right${loading ? ' fa-spin' : ''}`} />
                        Atualizar
                    </button>
                )}
            />

            {warningMessages.length > 0 && (
                <div className="alert alert-info dashboard-inline-alert mb-4">
                    <i className="fa-solid fa-circle-info me-2" />
                    {warningMessages.join(' ')}
                </div>
            )}

            <SectionBand
                title="Agora"
                hint="Posição do momento e histórico fechado. Não muda com o filtro de período."
            />

            {canSeeSubscription && (
                <HeadlineCard
                    title="Assinaturas"
                    hint="Dados de assinaturas da empresa inteira, mesmo com uma filial selecionada. Recebido no mês é a soma das mensalidades pagas, como no extrato do provedor. A comparação usa o mesmo intervalo do mês anterior."
                    loading={loadingSections.plans}
                    stats={[
                        {
                            label: 'Ativas',
                            icon: 'fa-id-card',
                            value: fmtNumber(planOverview.activePlans),
                        },
                        {
                            label: 'Vendidas no mês',
                            icon: 'fa-arrow-trend-up',
                            value: fmtNumber(planOverview.soldInMonth),
                            delta: (
                                <StatDelta
                                    current={planOverview.soldInMonth}
                                    previous={planOverview.soldPreviousMonth}
                                    note={previousMonthNote}
                                />
                            ),
                        },
                        {
                            label: 'Canceladas no mês',
                            icon: 'fa-user-slash',
                            value: fmtNumber(planOverview.canceledInMonth),
                            delta: (
                                <StatDelta
                                    current={planOverview.canceledInMonth}
                                    previous={planOverview.canceledPreviousMonth}
                                    note={previousMonthNote}
                                    higherIsBetter={false}
                                />
                            ),
                        },
                    ]}
                    footer={(
                        <div className="dashboard-headline__meta">
                            <span>
                                Recebido no mês
                                <strong>{fmtCurrency(planOverview.receivedInMonth)}</strong>
                            </span>
                            <span>
                                Mensalidade média
                                <strong>{fmtCurrency(planOverview.averagePaidValue)}</strong>
                            </span>
                        </div>
                    )}
                />
            )}

            <HeadlineCard
                title="Faturamento bruto"
                hint="Valores da filial selecionada ou de todas as filiais, conforme o filtro. Soma o que as comandas cobraram em dinheiro, cartão ou PIX às mensalidades recebidas, descontados cancelamentos e estornos. Com uma filial selecionada, as mensalidades entram pela parte dela: o total da empresa no mês repartido pelos atendimentos de assinatura feitos ali, porque o assinante pode ser atendido em qualquer unidade (a partir de outubro de 2026). A comanda fechada como assinatura ou cortesia conta zero, porque o dinheiro do assinante entra pela mensalidade. Mês e ano atuais vão até hoje; Histórico inclui todos os anos do recorte de filiais."
                loading={loadingSections.overview}
                stats={[
                    {
                        label: 'Mês atual',
                        icon: 'fa-calendar-day',
                        value: fmtCurrency(revenueOverview.monthlyRevenue),
                        delta: (
                            <StatDelta
                                current={revenueOverview.monthlyRevenue}
                                previous={revenueOverview.previousMonthRevenue}
                                note={previousMonthNote}
                                format={fmtCurrency}
                            />
                        ),
                    },
                    {
                        label: 'Ano atual',
                        icon: 'fa-calendar-days',
                        value: fmtCurrency(revenueOverview.yearRevenue),
                    },
                    {
                        label: 'Histórico',
                        icon: 'fa-sack-dollar',
                        value: fmtCurrency(revenueOverview.totalRevenue),
                    },
                ]}
                footer={canSeeSubscription && (
                    <>
                        <p className="dashboard-headline__footer-title">Composição do mês</p>
                        <CompositionMeter
                            segments={[
                                { label: 'Comandas', value: currentMonthPoint.orders, color: DASHBOARD_COLORS.markOrders },
                                { label: 'Assinaturas', value: currentMonthPoint.subscriptions, color: DASHBOARD_COLORS.markSubscriptions },
                                // Parcela da Receita do DRE lançada em contas a receber; com valor zero a parte some.
                                { label: 'Outras receitas', value: currentMonthPoint.otherRevenue, color: DASHBOARD_COLORS.purple },
                            ]}
                        />
                    </>
                )}
            />

            <Row className="g-3 mb-4">
                <Col xs={12}>
                    <SpotlightCard tone="sky">
                        <Card.Header>
                            <DashboardSectionHeader
                                icon="fa-chart-area"
                                accentClass="dashboard-accent-card dashboard-accent-card--sky"
                                eyebrow="Evolução do negócio"
                                title="Faturamento"
                                subtitle="Últimos 12 meses. Passe o mouse para ver o mês quebrado por origem do dinheiro e por cadeira."
                            />
                        </Card.Header>
                        <Card.Body>
                            {loadingSections.overview ? (
                                <PanelLoading height={360} label="Calculando o faturamento dos últimos 12 meses..." />
                            ) : revenueTimelineHasData ? (
                                <ResponsiveContainer width="100%" height={360}>
                                    <AreaChart data={revenueOverview.timeline} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="revenue-overview-fill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={DASHBOARD_COLORS.sky} stopOpacity={0.32} />
                                                <stop offset="95%" stopColor={DASHBOARD_COLORS.sky} stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke={axisGrid} vertical={false} />
                                        <XAxis dataKey="label" tick={{ fill: axisMuted, fontSize: 12 }} axisLine={false} tickLine={false} minTickGap={8} />
                                        <YAxis
                                            tick={{ fill: axisMuted, fontSize: 12 }}
                                            axisLine={false}
                                            tickLine={false}
                                            width={70}
                                            tickFormatter={fmtAxisCurrency}
                                        />
                                        <RTooltip content={<RevenueTooltip showSubscriptions={canSeeSubscription} />} />
                                        <Area
                                            type="monotone"
                                            dataKey="total"
                                            name="Faturamento"
                                            stroke={DASHBOARD_COLORS.skyStrong}
                                            strokeWidth={3}
                                            fill="url(#revenue-overview-fill)"
                                            dot={false}
                                            activeDot={{ r: 5, strokeWidth: 0, fill: DASHBOARD_COLORS.skyStrong }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState icon="fa-chart-area" title="Sem faturamento no histórico" description="A curva aparece assim que houver comandas ou assinaturas faturadas." />
                            )}
                        </Card.Body>
                    </SpotlightCard>
                </Col>
            </Row>

            <SectionBand
                title="Análise do período"
                hint={`Tudo daqui para baixo responde ao período selecionado: ${periodLabel}.`}
                sticky
                action={periodFilters}
            />

            <Row className="g-3 mb-4">
                <Col xl={3} md={6}>
                    <KpiCard
                        label="Receita avulsa"
                        loading={loadingSections.bi}
                        value={fmtCurrency(biData.revenue.current)}
                        growth={biData.revenue.growth}
                        icon="fa-sack-dollar"
                        accentClass="green"
                        context={`${fmtCurrency(biData.revenue.previous)} ${['month', 'lastMonth'].includes(periodPreset) ? 'no mês anterior' : 'no período anterior'}`}
                        sparkData={buildKpiSpark(biData.revenue)}
                    />
                </Col>

                <Col xl={3} md={6}>
                    <KpiCard
                        label="Comandas"
                        loading={loadingSections.bi}
                        value={fmtNumber(biData.orders.current)}
                        growth={biData.orders.growth}
                        icon="fa-receipt"
                        accentClass="sky"
                        context={`${fmtNumber(biData.orders.previous)} no período anterior`}
                        sparkData={buildKpiSpark(biData.orders)}
                    />
                </Col>

                <Col xl={3} md={6}>
                    <KpiCard
                        label="Novos clientes"
                        loading={loadingSections.bi}
                        value={fmtNumber(biData.customers.current)}
                        growth={biData.customers.growth}
                        icon="fa-user-plus"
                        accentClass="teal"
                        context={`${daysInRange} dias analisados`}
                        sparkData={buildKpiSpark(biData.customers)}
                    />
                </Col>

                <Col xl={3} md={6}>
                    <KpiCard
                        label="Ticket médio"
                        loading={loadingSections.bi}
                        value={fmtCurrency(biData.avgTicket.current)}
                        growth={biData.avgTicket.growth}
                        icon="fa-receipt"
                        accentClass="amber"
                        context={fmtCurrency(biData.avgTicket.previous)}
                        sparkData={buildKpiSpark(biData.avgTicket)}
                    />
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xs={12}>
                    <SpotlightCard tone="teal">
                        <Card.Header>
                            <DashboardSectionHeader
                                icon="fa-chair"
                                accentClass="dashboard-accent-card dashboard-accent-card--teal"
                                eyebrow="Capacidade da agenda"
                                title="Ocupação das cadeiras"
                                subtitle="Tempo agendado sobre o tempo em que cada barbeiro estava disponível: expediente nos dias em que trabalha, sem almoço e sem bloqueios. Conta só o que já passou."
                            />
                        </Card.Header>
                        <Card.Body>
                            {loadingSections.bi ? (
                                <PanelLoading height={220} label="Calculando a ocupação das cadeiras..." />
                            ) : (
                            <>
                            {branchesWithoutHours.length > 0 && (
                                <div className="alert alert-warning dashboard-inline-alert mb-3">
                                    <i className="fa-solid fa-triangle-exclamation me-2" />
                                    {branchesWithoutHours.length === 1 ? 'A filial ' : 'As filiais '}
                                    <strong>{branchesWithoutHours.join(', ')}</strong>
                                    {branchesWithoutHours.length === 1 ? ' está fora do cálculo porque não tem' : ' estão fora do cálculo porque não têm'} horário de funcionamento cadastrado.
                                </div>
                            )}
                            <div className="dashboard-occupancy">
                                <div className="dashboard-occupancy__figure">
                                    <strong>{fmtPercent(occupancy.rate)}</strong>
                                    <StatDelta
                                        current={occupancy.rate}
                                        previous={occupancy.previousRate}
                                        note="vs. mesmo trecho do período anterior"
                                        format={(value) => `${value.toFixed(1)} p.p.`}
                                    />
                                </div>

                                <div className="dashboard-occupancy__detail">
                                    {/* A barra mostra o mesmo percentual em forma de ocupação da
                                        capacidade: cheio é agenda lotada, vazio é cadeira parada. */}
                                    <div className="dashboard-occupancy__track">
                                        <span style={{ width: `${Math.min(occupancy.rate || 0, 100)}%` }} />
                                    </div>
                                    <div className="dashboard-occupancy__legend">
                                        <span>Ocupadas<strong>{fmtNumber(occupiedHours)}h</strong></span>
                                        <span>Ociosas<strong>{fmtNumber(idleHours)}h</strong></span>
                                        <span>Capacidade<strong>{fmtNumber(availableHours)}h</strong></span>
                                        <span>Bloqueadas<strong>{fmtNumber(blockedHours)}h</strong></span>
                                        <span>Barbeiros<strong>{fmtNumber(occupancy.serviceProviders)}</strong></span>
                                    </div>
                                </div>
                            </div>

                            {occupancy.pendingAppointments > 0 && (
                                <p className="dashboard-occupancy__note">
                                    <i className="fa-solid fa-circle-info" />
                                    {fmtNumber(occupancy.pendingAppointments)} {occupancy.pendingAppointments === 1 ? 'agendamento que já passou está' : 'agendamentos que já passaram estão'} sem baixa e contam como ocupados. Marque como concluído ou falta para a taxa ficar exata.
                                </p>
                            )}

                            {occupancyBranches.length > 1 && (
                                <div className="dashboard-occupancy-branches">
                                    {occupancyBranches.map((branch) => {
                                        const zone = getOccupancyZone(branch.rate || 0);
                                        return (
                                            <div key={branch.branchId} className="dashboard-occupancy-branches__item">
                                                <span>{branch.branchName}</span>
                                                <strong>{fmtPercent(branch.rate)}</strong>
                                                <small className={`dashboard-occupancy-zone--${zone.key}`}>{zone.label}</small>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {occupancyEmployees.length > 0 && (
                                <Row className="g-4 mt-1">
                                    <Col lg={5}>
                                        <p className="dashboard-ranking__title">
                                            <i className="fa-solid fa-user" />
                                            Por barbeiro
                                        </p>
                                        <EmployeeOccupancyList employees={occupancyEmployees} showBranch={occupancyBranches.length > 1} />
                                    </Col>
                                    <Col lg={7}>
                                        <p className="dashboard-ranking__title">
                                            <i className="fa-solid fa-table-cells" />
                                            Por dia e horário
                                        </p>
                                        <OccupancyHeatmap cells={occupancyHeatmap} />
                                    </Col>
                                </Row>
                            )}
                            </>
                            )}
                        </Card.Body>
                    </SpotlightCard>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xs={12}>
                    <SpotlightCard tone="sky">
                        <Card.Header>
                            <DashboardSectionHeader
                                icon="fa-chart-column"
                                accentClass="dashboard-accent-card dashboard-accent-card--sky"
                                eyebrow="Performance comercial"
                                title="Faturamento por dia da semana"
                                subtitle="Soma do faturamento do período selecionado por dia da semana."
                            />
                        </Card.Header>
                        <Card.Body>
                            {loadingSections.bi ? (
                                <PanelLoading height={280} />
                            ) : trendData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={trendData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={axisGrid} vertical={false} />
                                        <XAxis dataKey="day" tick={{ fill: axisMuted, fontSize: 12 }} axisLine={false} tickLine={false} />
                                        <YAxis
                                            width={64}
                                            tick={{ fill: axisMuted, fontSize: 12 }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={fmtAxisCurrency}
                                        />
                                        <RTooltip cursor={{ fill: 'rgba(252, 128, 25, 0.08)' }} content={<TooltipCard valueFormatter={(value) => fmtCurrency(value)} />} />
                                        <Bar dataKey="revenue" name="Faturamento" fill={DASHBOARD_COLORS.teal} radius={[8, 8, 0, 0]} barSize={24} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState icon="fa-chart-column" title="Sem movimentação" description="Ainda não existem vendas para montar a tendência semanal." />
                            )}
                        </Card.Body>
                    </SpotlightCard>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xs={12}>
                    <SpotlightCard tone="purple">
                        <Card.Header>
                            <DashboardSectionHeader
                                icon="fa-clock"
                                accentClass="dashboard-accent-card dashboard-accent-card--purple"
                                eyebrow="Distribuição operacional"
                                title="Intensidade por horário"
                                subtitle="Faturamento por hora de registro da comanda. Passe o mouse para ver quantos atendimentos entraram."
                            />
                        </Card.Header>
                        <Card.Body>
                            {loadingSections.bi ? (
                                <PanelLoading height={260} />
                            ) : hourlyData.length > 0 ? (
                                // Barras e não área: cada hora é uma faixa fechada, e a curva suavizada
                                // inventava movimento entre um horário vazio e o seguinte.
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={hourlyData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={axisGrid} vertical={false} />
                                        <XAxis dataKey="hour" tick={{ fill: axisMuted, fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={8} />
                                        <YAxis
                                            width={64}
                                            tick={{ fill: axisMuted, fontSize: 12 }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={fmtAxisCurrency}
                                        />
                                        <RTooltip cursor={{ fill: 'rgba(252, 128, 25, 0.08)' }} content={<RevenueWithCountTooltip countKey="count" />} />
                                        <Bar dataKey="revenue" name="Faturamento" fill={DASHBOARD_COLORS.amber} radius={[8, 8, 0, 0]} maxBarSize={28} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState icon="fa-clock" title="Sem histórico horário" description="Ainda não há dados suficientes para desenhar a distribuição ao longo do dia." />
                            )}
                        </Card.Body>
                    </SpotlightCard>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xs={12}>
                    <SpotlightCard tone="teal">
                        <Card.Header>
                            <DashboardSectionHeader
                                icon="fa-user-tie"
                                accentClass="dashboard-accent-card dashboard-accent-card--teal"
                                eyebrow="Produção da equipe"
                                title="Produção por colaborador"
                                subtitle="Produção total no período, com comandas e parcela de assinaturas por pontuação."
                                action={(
                                    <div className="dashboard-employee-filter">
                                        <label htmlFor="dashboard-employee-filter">Colaborador</label>
                                        <EmployeeSelect
                                            branch={selectedBranch}
                                            value={employeeProductionFilter}
                                            onChange={setEmployeeProductionFilter}
                                            filterProvidesService={false}
                                        />
                                    </div>
                                )}
                            />
                        </Card.Header>
                        <Card.Body>
                            {loadingSections.bi ? (
                                <PanelLoading height={240} label="Calculando a produção da equipe..." />
                            ) : employeeProductionData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={64 + employeeProductionData.length * 46}>
                                    {/* A margem à direita acomoda o rótulo com centavos, que é o que
                                        o gestor confere contra o relatório de comissões. */}
                                    <BarChart data={employeeProductionData} layout="vertical" margin={{ top: 0, right: 112, left: 8, bottom: 0 }}>
                                        {/* Cada barra já leva o valor na ponta: eixo e grade só repetiriam o número. */}
                                        <XAxis type="number" hide />
                                        <YAxis type="category" dataKey="name" width={150} tick={{ fill: axisInk, fontSize: 12 }} axisLine={false} tickLine={false} />
                                        <RTooltip cursor={{ fill: 'rgba(252, 128, 25, 0.08)' }} content={<EmployeeProductionTooltip />} />
                                        <Bar dataKey="receita" name="Produção total" radius={[0, 8, 8, 0]} barSize={18} onClick={openEmployeeProductionDetail} cursor="pointer">
                                            {employeeProductionData.map((employee) => (
                                                <Cell key={employee.name} fill={employee.color} />
                                            ))}
                                            {/* Uma série só: o valor vai na ponta da barra e dispensa legenda. */}
                                            <LabelList
                                                dataKey="receita"
                                                position="right"
                                                formatter={(value) => fmtCurrency(value)}
                                                style={{ fill: axisMuted, fontSize: 12, fontWeight: 700 }}
                                            />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <EmptyState icon="fa-user-tie" title="Sem produção no período" description="O ranking aparece assim que houver atendimentos lançados em comanda." />
                            )}
                        </Card.Body>
                    </SpotlightCard>
                </Col>
            </Row>

            <Modal
                show={Boolean(employeeProductionDetail)}
                onHide={() => setEmployeeProductionDetail(null)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        Detalhamento da produção{employeeProductionDetail?.production?.name
                            ? ` — ${employeeProductionDetail.production.name}` : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {employeeProductionDetailLoading || !employeeProductionDetail?.detail ? (
                        <PanelLoading height={200} label="Carregando composição da produção..." />
                    ) : (
                        <EmployeeProductionDetail
                            detail={employeeProductionDetail.detail}
                            production={employeeProductionDetail.production}
                        />
                    )}
                </Modal.Body>
            </Modal>

            <Row className="g-3 mb-4">
                <Col xs={12}>
                    <SpotlightCard tone="amber">
                        <Card.Header>
                            <DashboardSectionHeader
                                icon="fa-award"
                                accentClass="dashboard-accent-card dashboard-accent-card--amber"
                                eyebrow="Serviços e produtos"
                                title="Serviços e produtos líderes"
                                subtitle="Os cinco que mais faturaram em cada tipo no período. O percentual é sobre o faturamento do próprio tipo."
                            />
                        </Card.Header>
                        <Card.Body>
                            {loadingSections.bi ? (
                                <PanelLoading height={240} />
                            ) : biData.topServices.length > 0 || biData.topProducts.length > 0 ? (
                                <>
                                    <CompositionMeter
                                        segments={[
                                            { label: 'Serviços', value: biData.serviceRevenue, color: DASHBOARD_COLORS.markOrders },
                                            { label: 'Produtos', value: biData.productRevenue, color: DASHBOARD_COLORS.markSubscriptions },
                                            // Reserva é receita de vendas sem item: com ela a barra soma o mesmo que
                                            // as Comandas. Sem reserva no período a parte some sozinha (valor zero).
                                            { label: 'Reservas', value: biData.depositRevenue, color: DASHBOARD_COLORS.purple },
                                        ]}
                                    />
                                    <Row className="g-4 mt-1">
                                        <Col lg={6}>
                                            <ItemRanking
                                                title="Serviços"
                                                icon="fa-scissors"
                                                items={biData.topServices}
                                                emptyText="Nenhum serviço vendido no período."
                                            />
                                        </Col>
                                        <Col lg={6}>
                                            <ItemRanking
                                                title="Produtos"
                                                icon="fa-bottle-droplet"
                                                items={biData.topProducts}
                                                emptyText="Nenhum produto vendido no período."
                                            />
                                        </Col>
                                    </Row>
                                </>
                            ) : (
                                <EmptyState icon="fa-award" title="Sem ranking ainda" description="Quando houver vendas no período, os serviços e produtos que mais faturaram aparecerão aqui." />
                            )}
                        </Card.Body>
                    </SpotlightCard>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                <Col xs={12}>
                    <SpotlightCard tone="sky">
                        <Card.Header>
                            <DashboardSectionHeader
                                icon="fa-chart-pie"
                                accentClass="dashboard-accent-card dashboard-accent-card--sky"
                                eyebrow="Mix de vendas"
                                title="Receita por categoria"
                                subtitle="Onde o faturamento das comandas está concentrado, por categoria do item."
                            />
                        </Card.Header>
                        <Card.Body>
                            {loadingSections.bi ? (
                                <PanelLoading height={240} />
                            ) : biData.topCategories.length > 0 ? (
                                // Com a linha inteira, a rosca fica à esquerda e a lista ao lado mostra todas
                                // as categorias; no celular as duas partes empilham.
                                <Row className="g-4 align-items-center">
                                    <Col md={5}>
                                        <ResponsiveContainer width="100%" height={240}>
                                            <PieChart>
                                                <Pie
                                                    data={biData.topCategories}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={68}
                                                    outerRadius={96}
                                                    dataKey="value"
                                                    paddingAngle={2}
                                                    strokeWidth={0}
                                                >
                                                    {biData.topCategories.map((item, index) => (
                                                        <Cell key={`${item.name}-${index}`} fill={item.color} />
                                                    ))}
                                                </Pie>
                                                <RTooltip content={<TooltipCard valueFormatter={(value) => fmtCurrency(value)} />} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Col>
                                    <Col md={7}>
                                        <div className="dashboard-ranking-list">
                                            {biData.topCategories.map((category) => (
                                                <div key={category.name} className="dashboard-ranking-list__row">
                                                    <div className="dashboard-ranking-list__label">
                                                        <span className="dashboard-ranking-list__dot" style={{ background: category.color }} />
                                                        <span>{category.name}</span>
                                                    </div>
                                                    <div className="dashboard-ranking-list__value">
                                                        <strong>{fmtCurrency(category.value)}</strong>
                                                        <small>{fmtPercent(category.share)}</small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Col>
                                </Row>
                            ) : (
                                <EmptyState icon="fa-chart-pie" title="Sem categorias no período" description="Assim que houver faturamento, a distribuição por categoria aparecerá aqui." />
                            )}
                        </Card.Body>
                    </SpotlightCard>
                </Col>
            </Row>

        </div>
    );
};

export default Home;
